// Stage 48 §17.4: side drawer that paginates audit-log rows for a single
// connection. Cursor pagination via the `before` query param; rows are
// reverse-chronological.

import { useEffect, useState } from 'react';
import { listAudit, type AuditRow } from '../api/connections';

interface Props {
  connectionId: string;
  connectionLabel: string;
  onClose: () => void;
}

function formatTs(unix: number): string {
  return new Date(unix * 1000).toLocaleString();
}

function formatBytes(b: number | null): string {
  if (b == null) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

export function ConnectionAuditDrawer({
  connectionId,
  connectionLabel,
  onClose,
}: Props) {
  const [rows, setRows] = useState<AuditRow[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  async function loadInitial(): Promise<void> {
    setBusy(true);
    setErr(null);
    try {
      const res = await listAudit(connectionId);
      setRows(res.rows);
      setCursor(res.nextCursor);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function loadMore(): Promise<void> {
    if (!cursor) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await listAudit(connectionId, parseInt(cursor, 10));
      setRows((prev) => (prev ? [...prev, ...res.rows] : res.rows));
      setCursor(res.nextCursor);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void loadInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionId]);

  // Esc-to-close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function copyAsJson(): void {
    if (!rows) return;
    void navigator.clipboard.writeText(JSON.stringify(rows, null, 2));
  }

  return (
    <aside
      className="audit-drawer audit-drawer-open"
      role="dialog"
      aria-modal="true"
      aria-label={`Audit log for ${connectionLabel}`}
    >
      <header className="audit-drawer-header">
        <div>
          <h2>Audit log</h2>
          <p className="muted small">{connectionLabel}</p>
        </div>
        <div className="row">
          <button onClick={copyAsJson} disabled={!rows || rows.length === 0}>
            Copy as JSON
          </button>
          <button onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
      </header>
      <div className="audit-drawer-body">
        {rows === null ? (
          <div className="muted">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="muted">No requests recorded yet.</div>
        ) : (
          <ul className="audit-list">
            {rows.map((r) => {
              const expanded = expandedId === r.id;
              const errCls = r.error_kind ? 'audit-error' : '';
              return (
                <li
                  key={r.id}
                  className={`audit-row ${errCls}`}
                  onClick={() =>
                    setExpandedId((prev) => (prev === r.id ? null : r.id))
                  }
                >
                  <div className="audit-summary">
                    <span className="audit-ts">{formatTs(r.ts)}</span>
                    <span className="audit-method">{r.method}</span>
                    <span className="audit-path">
                      {r.path.split('?')[0]}
                    </span>
                    <span className="audit-status">{r.status_code}</span>
                    <span className="audit-bytes">
                      {formatBytes(r.bytes_returned)}
                    </span>
                    <span className="audit-origin muted small">
                      {r.origin || ''}
                    </span>
                    {r.error_kind ? (
                      <span className="audit-error-kind">
                        {r.error_kind}
                      </span>
                    ) : null}
                  </div>
                  {expanded ? (
                    <div className="audit-detail muted small">
                      <div>
                        <strong>Path:</strong> {r.path}
                      </div>
                      <div>
                        <strong>Request ID:</strong> {r.request_id}
                      </div>
                      <div>
                        <strong>User-Agent:</strong> {r.user_agent}
                      </div>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
        {err ? <div className="status-missing">Error: {err}</div> : null}
        {cursor ? (
          <div className="row" style={{ marginTop: 12 }}>
            <button onClick={() => void loadMore()} disabled={busy}>
              {busy ? 'Loading…' : 'Load more'}
            </button>
          </div>
        ) : null}
      </div>
    </aside>
  );
}
